import { useCallback, useEffect, useMemo, useState } from "react"
import DataTable from "../../common/components/DataTable";
import { useTranslation } from "react-i18next";
import Select from "../../common/components/Select";
import { Row, Col } from "../../common/components/Grid";
import PropTypes from 'prop-types';
import { deepEquals } from "../../common/utils/objectUtilities";
import { DefaultButton } from "../../common/components/DefaultButton";
import Card from "../../common/components/Card";
import useClientConfig from "../../common/hooks/clientConfigHook";
import TypeDropdown, { RoleDropdown } from "../TypeDropdown";
import { useTypes } from "../../reducers/typeSlice";
import { ensureArray } from "../../common/utils/arrayUtilities";
import AccessDropdown from "../AccessDropdown";
import { deepClone } from "../../common/utils/objectUtilities";

const ORDINARY_USER = 'ordinaryUser';

const PermissionsInput = ({title, values, adminPositions, access, required = false, disabled = false, readonly = false, onApply, onChange, isRoleBased = false, excludeTypes = []}) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [data, setData] = useState(deepClone(ensureArray(values)));
    const [roleType, setRoleType] = useState();
    const types = useTypes();

    const displayData = useMemo(() => {
        if (excludeTypes.length === 0) {
            return data;
        }
        return data.filter(d => !excludeTypes.includes(d.type));
    }, [data, excludeTypes])
    
    const isAdmin = useCallback(type => adminPositions.length > 0 && type && adminPositions.includes(type.toLowerCase()), [adminPositions])

    const processValues = useCallback((values, callback) => {
        let data = null;
        if (!values) {
            data = []
        } else if (Array.isArray(values)) {
            data = deepClone(values);
        } else {
            data = [deepClone(values)];
        }
        if (data.length > 0) {
            if (isAdmin(data[0].type)) {
                setRoleType(data[0].type);
            } else {
                setRoleType(ORDINARY_USER);
            }
        }
        setData(data);
        callback?.(data);
    }, [isAdmin])

    useEffect(() => {
        processValues(values);
    }, [values, processValues])

    const roleTypes = useMemo(() => adminPositions.concat(ORDINARY_USER), [adminPositions])
    
    const isUpdated = useMemo(() => !deepEquals(values, data), [values, data]);
    const isOrdinary = useMemo(() => {
        if (adminPositions.length === 0) {
            return true;
        }
        return !!(roleType && !adminPositions.includes(roleType));
    }, [adminPositions, roleType])
    

    const handleRoleTypeChange = role => {
        processValues([], onChange);
        setRoleType(role);
    }

    const handleAdminAccessChange = access => {
        processValues([{type: roleType, access: parseInt(access) }], onChange);
    }

    const apply = () => {
        onApply?.(data);
    }

    const columns = useMemo(() => {
        let columns = [{ type: 'type', id: 'type', field: ['type','site','level'], title: t('type', { count: 1 }), label: t('type', { count: 1 }), readonly: true, wildcard: t('anyWildcard') }];
        if (clientConfig.enableLevels) {
            columns.unshift({ type: 'level', id: 'level', field: ['level','site','type'], title: t('level', { count: 1 }), label: t('level', { count: 1 }), readonly: true, wildcard: t('anyWildcard') })
        }
        if (clientConfig.enableSites) {
            columns.unshift({ type: 'site', id: 'site', field: ['site','type','level'], title: t('site', { count: 1 }), label: t('site', { count: 1 }), readonly: true, wildcard: t('anyWildcard') })
        }
        if (access.length > 0) {
            columns.push({ type: 'access', id: 'access', field: 'access', readonly: true, title: t('access'), access, })
            //columns.push({type: 'option', id: 'access', field: 'access', readonly: true, title: t('access'), options: accessOptions, key: 'key', value: 'value'})  //TODO warning, the field 'key' is overloaded - it is used by CustomCtrl and DataTable for different purposes
        }
        return columns;
    }, [access, t, clientConfig.enableLevels, clientConfig.enableSites])

    const footerColumns = useMemo(() => {
        const BaseDropdown = isRoleBased ? RoleDropdown : TypeDropdown
        let columns = [{type: 'dynamic', id: 'type', field: ['type', 'site', 'level'], title: t('role_one'), def: (value, disabled, hideLabel, onChange) => <BaseDropdown disabled={disabled} wildcard={t('allWildcard')} site={value.site} level={value.level} type={value.type} hideLabel={true} strict={false} onChange={value => {
            if (value) {
                Object.keys(value).filter(f => ['site','level','type'].includes(f)).forEach(f => onChange(value[f], f))
            }
        }}></BaseDropdown>}];
        if (clientConfig.enableLevels) {
            columns.unshift({ type: 'level', id: 'level', field: ['level','site','type'], title: t('level', { count: 1 }), label: t('level', { count: 1 }), wildcard: t('anyWildcard'), strict: false })
        }
        if (clientConfig.enableSites) {
            columns.unshift({ type: 'site', id: 'site', field: ['site','type','level'], title: t('site', { count: 1 }), label: t('site', { count: 1 }), wildcard: t('anyWildcard'), strict: false })
        }
        if (access.length > 0) {
            columns.push({ type: 'access', id: 'access', field: 'access', title: t('access'), label: t('access'), required: true, editable: true, access });
            //columns.push({type: 'option', id: 'access', field: 'access', title: t('access'), label: t('access'), required: true, editable: true, options: accessOptions, key: 'key', value: 'value'})  //TODO warning, the field 'key' is overloaded - it is used by CustomCtrl and DataTable for different purposes
        }
        return columns;
    }, [access, t, clientConfig.enableLevels, clientConfig.enableSites, isRoleBased])

    
    const isValid = useMemo(() => {
        return data.length > 0 && data.every(r => Number.isInteger(r?.access));
    }, [data]);
    
    const footerControls = disabled || readonly || !onApply ? null : <Row>
            <Col></Col>
            <Col xs='auto' className="pr-2"><DefaultButton disabled={!isUpdated} kind='clear' onClick={() => processValues(deepClone(values))}></DefaultButton></Col>
            <Col xs='auto'><DefaultButton kind="apply" disabled={!isUpdated || !isValid} onClick={apply}></DefaultButton></Col>
        </Row>

    const handleTableAdd  = (newRow) => {
        newRow.access = parseInt(newRow.access ?? 0);
        if (!newRow.type || (clientConfig.enableSites && !newRow.site) || (clientConfig.enableLevels && !newRow.level)) {
            setData(data => {
                data = data.concat(types.filter(t => (t.type === newRow.type || !newRow.type) && (!clientConfig.enableSites || !newRow.site || !t.site || t.site === newRow.site) && (!clientConfig.enableLevels || !newRow.level || !t.level || t.level === newRow.level)).map(type => ({...newRow, site: newRow.site ?? type.site, level: newRow.level ?? type.level, type: type.type })));
                onChange?.(data);
                return data;
            });
        } else {
            setData(data => {
                data = [...data, { ...newRow }]
                onChange?.(data);
                return data;
            });
        }
    }

    const handleTableRemove = (toRemove) => {
        setData(data => {
            data = data.filter(d => !deepEquals(d, toRemove))
            onChange?.(data);
            return data;
        });
    }

    const handleTableUpdate = (row, field, value) => {
        if (field === 'access') {
            value = parseInt(value);
        }
        const updated = { ...row, [field]: value };
        setData(data => {
            data = data.map((v) => v !== row  ? v : updated)
            onChange?.(data);
            return data;
        });
    }

    const roleTypeOptions = useMemo(() => roleTypes.map(rt => ({ value: rt, label: t(rt) })), [roleTypes, t])

    return <Card title={title} footerControls={footerControls}>
        {(disabled || readonly) && values.length === 0 ? <Row><Col>{t('none')}</Col></Row> :
            <>
                <Row>
                    <Col>
                        {adminPositions.length > 0 ? <Select required={required} readonly={readonly} disabled={disabled} placeholder={t('roleType')} value={roleType} onChange={handleRoleTypeChange} options={roleTypeOptions} labelField="label" valueField="value"></Select> : null}
                    </Col>
                    {isAdmin(roleType) && <Col>
                        <AccessDropdown value={data[0]?.access} required={required} access={access} readonly={readonly} disabled={disabled} onChange={handleAdminAccessChange}></AccessDropdown>
                    </Col>}


                </Row>
                {isOrdinary && <DataTable required={required} columns={columns} footerColumns={footerColumns} value={displayData} disabled={disabled} readonly={readonly} onUpdate={handleTableUpdate} onRemove={handleTableRemove} onAdd={handleTableAdd}></DataTable> }
            </>
        }
    </Card>
}

PermissionsInput.propTypes = {
    title: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.object).isRequired,
    adminPositions: PropTypes.arrayOf(PropTypes.string).isRequired,
    access: PropTypes.arrayOf(PropTypes.number).isRequired,
    disabled: PropTypes.bool,
    readonly: PropTypes.bool,
    isRoleBased: PropTypes.bool,
    onApply: PropTypes.func
}

export default PermissionsInput;

const excludeTypes = ['sitrep'];

export const UserPermissionsInput = ({ title, values, required, disabled, readonly, onChange, onApply }) => {
    const { t } = useTranslation();
    // we exclude sitrep permissions from dislplaying as they are a special case and should not be visible in the general permissions editor
    return <PermissionsInput excludeTypes={excludeTypes} title={title ?? t('role_other')} isRoleBased={true} disabled={disabled} required={required} readonly={readonly} onChange={onChange} onApply={onApply} values={values} access={[0,1,2]} adminPositions={['administrator','dispatcher','stats']}></PermissionsInput>
}

export const LogPermissionsInput = ({ title, values, required, disabled, readonly, onChange, onApply }) => {
    const { t } = useTranslation();
    return <PermissionsInput title={title ?? t('log_one')} disabled={disabled} required={required} readonly={readonly} onChange={onChange} onApply={onApply} values={values} access={[0,1]} adminPositions={[]}></PermissionsInput>
}

export const SitRepPermissionsInput = ({ title, values, required, disabled, readonly, onChange, onApply }) => {
    const { t } = useTranslation();
    return <PermissionsInput title={title ?? t('sitrep')} disabled={disabled} required={required} readonly={readonly} onChange={onChange} onApply={onApply} values={values} access={[0,1]} adminPositions={[]}></PermissionsInput>
}